import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Button, Input } from '@heroui/react';
import { useAuth } from '../../../context/AuthContext';
import userService from '../../../services/user.service';
import publicService from '../../../services/public.service';
import developerService from '../../../services/developer.service';
import LoadingSpinner from '../../../components/LoadingSpinner';
// import Dock from '../components/Dock'; // Switching to Sidebar as requested
import Sidebar from '../components/Sidebar';
import { BarChartIcon, ClipboardIcon, MicroscopeIcon, CogIcon, CodeIcon } from '../icons';

const UserDashboard = () => {
  const { user, logout, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [insurancePlans, setInsurancePlans] = useState([]);
  const [providers, setProviders] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
  });

  const [visibleKeys, setVisibleKeys] = useState({});

  const toggleKeyVisibility = (id) => {
    setVisibleKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopyKey = (key) => {
    if (!key) return;
    navigator.clipboard.writeText(key);
    alert('API Key copied to clipboard!');
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: <BarChartIcon className="w-6 h-6" /> },
    { id: 'developers', label: 'Developers', icon: <CodeIcon className="w-6 h-6" /> },
    { id: 'profile', label: 'Profile', icon: <CogIcon className="w-6 h-6" /> },
  ];

  const loadData = React.useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      switch (activeTab) {
        case 'profile':
          const userData = await userService.getProfile();
          setProfileData({
            fullName: userData.fullName || '',
            email: userData.email || '',
          });
          break;
        case 'developers':
          const keys = await developerService.getApiKeys();
          setApiKeys(Array.isArray(keys) ? keys : []);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Error loading data:', err);
      if (err?.status === 401) {
        setError('Session expired. Please log out and log in again.');
      } else {
        setError(err?.message || 'Failed to load data');
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpdateProfile = async () => {
    try {
      const updated = await userService.updateProfile(profileData);
      updateUser(updated);
      alert('Profile updated successfully!');
    } catch (err) {
      alert('Failed to update profile: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      alert('Please enter a name for your API Key');
      return;
    }
    try {
      await developerService.createApiKey(newKeyName);
      setNewKeyName('');
      loadData(); // Reload keys
      alert('API Key created successfully!');
    } catch (err) {
      alert('Failed to create API key: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleRevokeApiKey = async (id) => {
    if (window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      try {
        await developerService.revokeApiKey(id);
        loadData();
      } catch (err) {
        alert('Failed to revoke API key: ' + (err?.message || 'Unknown error'));
      }
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="py-4">
            <h2 className="text-xl font-semibold mb-4">Welcome to Taasa</h2>
            <p className="text-gray-600 mb-4">
              Create an api key and access taasa services.
            </p>
          </div>
        );
      case 'insurance':
        return (
          <div className="py-4">
            {isLoading ? (
              <LoadingSpinner message="Loading insurance plans..." />
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-semibold mb-4">Available Insurance Plans</h2>
                {insurancePlans.length === 0 ? (
                  <p className="text-gray-600">No insurance plans available at this time.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {insurancePlans.map((plan) => (
                      <Card key={plan.id}>
                        <CardHeader>
                          <h3 className="font-semibold">{plan.name}</h3>
                        </CardHeader>
                        <CardBody>
                          <p className="text-sm text-gray-600 mb-2">{plan.description}</p>
                          <div className="text-sm">
                            <div className="font-semibold text-blue-600">
                              ${plan.price || 'N/A'} / {plan.interval || 'month'}
                            </div>
                          </div>
                          <Button color="primary" size="sm" className="mt-3">
                            Learn More
                          </Button>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case 'providers':
        return (
          <div className="py-4">
            {isLoading ? (
              <LoadingSpinner message="Loading providers..." />
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-semibold mb-4">Healthcare Providers</h2>
                {providers.length === 0 ? (
                  <p className="text-gray-600">No providers available.</p>
                ) : (
                  <div className="space-y-3">
                    {providers.map((provider) => (
                      <Card key={provider.id}>
                        <CardBody>
                          <div>
                            <div className="font-semibold text-lg">{provider.name}</div>
                            <div className="text-sm text-gray-600 mt-1">
                              Email: {provider.contactEmail}
                            </div>
                            <div className="text-sm text-gray-600">
                              License: {provider.licenseNumber}
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case 'developers':
        return (
          <div className="py-4 space-y-8">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">API Overview</h2>
                <p className="text-sm text-gray-500 mt-1">Manage your API keys and developer resources.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Test Mode
                </div>
              </div>
            </div>

            {/* Quick Actions / Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-white shadow border border-gray-100">
                <CardBody className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">How to use our API</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    Use our robust API to integrate ambulance requests, insurance verification, and provider lookups directly into your application.
                  </p>
                  <Button size="sm" color="secondary" variant="flat">View Documentation</Button>
                </CardBody>
              </Card>

              <Card className="bg-white shadow border border-gray-100">
                <CardBody className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Create New Key</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Generate a new secure key for your application.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      size="sm"
                      placeholder="Key Label (e.g. iOS App)"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      className="flex-1"
                    />
                    <Button size="sm" color="primary" onClick={handleCreateApiKey}>
                      Create
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* API Keys Table Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">API Keys</h3>
                <Button size="sm" variant="light" color="primary">Revoke All</Button>
              </div>

              {isLoading ? (
                <LoadingSpinner message="Loading API keys..." />
              ) : error ? (
                <div className="p-4 bg-red-50 text-red-600 rounded-lg">{error}</div>
              ) : apiKeys.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                  <p className="text-gray-500">No API keys active. Create one to get started.</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-4 p-4 border-b border-gray-100 bg-gray-50/50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <div className="col-span-3">Label</div>
                    <div className="col-span-5">API Key</div>
                    <div className="col-span-2">Created</div>
                    <div className="col-span-1">Status</div>
                    <div className="col-span-1 text-right">Action</div>
                  </div>

                  {/* Table Body */}
                  <div className="divide-y divide-gray-100">
                    {apiKeys.map((key) => {
                      // Logic for masking
                      const isVisible = visibleKeys[key.id];
                      const displayKey = isVisible ? key.key : (key.key ? 'pk_' + '*'.repeat(24) : 'pk_************************');

                      return (
                        <div key={key.id} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-gray-50/50 transition-colors duration-150">
                          <div className="col-span-3 font-medium text-gray-900">{key.name}</div>
                          <div className="col-span-5 flex items-center gap-2">
                            <div className="font-mono text-xs text-gray-600 bg-gray-100 p-1.5 rounded w-fit max-w-full truncate border border-gray-200">
                              {displayKey}
                            </div>
                            <button
                              onClick={() => toggleKeyVisibility(key.id)}
                              className="text-gray-400 hover:text-gray-600 p-1"
                              title={isVisible ? "Hide Key" : "Show Key"}
                            >
                              {isVisible ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleCopyKey(key.key)}
                              className="text-gray-400 hover:text-blue-600 p-1"
                              title="Copy to Clipboard"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                            </button>
                          </div>
                          <div className="col-span-2 text-sm text-gray-500">
                            {new Date(key.createdAt).toLocaleDateString()}
                          </div>
                          <div className="col-span-1">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                              Active
                            </span>
                          </div>
                          <div className="col-span-1 text-right">
                            <Button
                              isIconOnly
                              size="sm"
                              color="danger"
                              variant="light"
                              aria-label="Revoke Key"
                              onClick={() => handleRevokeApiKey(key.id)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 'profile':
        return (
          <div className="py-4">
            <h2 className="text-xl font-semibold mb-4">Profile Settings</h2>
            <div className="space-y-4 max-w-md">
              <Input
                label="Full Name"
                value={profileData.fullName}
                onChange={(e) =>
                  setProfileData({ ...profileData, fullName: e.target.value })
                }
              />
              <Input label="Email" value={profileData.email} type="email" readOnly />
              <div className="text-sm text-gray-500">
                Role: <span className="font-semibold">{user?.role}</span>
              </div>
              <Button color="primary" onClick={handleUpdateProfile}>
                Save Changes
              </Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // ... (rest of helper functions)

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar Navigation */}
      <Sidebar
        items={sidebarItems}
        activeId={activeTab}
        onSelect={setActiveTab}
        collapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      {/* Main Content Area */}
      <div className="flex-1 p-8 overflow-y-auto h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Dashboard</h1>
              <p className="text-gray-600 mt-1">Welcome, {user?.fullName || user?.email}</p>
            </div>
            <Button color="danger" variant="light" onClick={logout}>
              Logout
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardBody>
                <div className="text-sm text-gray-600">Insurance Plans</div>
                <div className="text-2xl font-bold">{insurancePlans.length}</div>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <div className="text-sm text-gray-600">Providers</div>
                <div className="text-2xl font-bold">{providers.length}</div>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <div className="text-sm text-gray-600">API Keys</div>
                <div className="text-2xl font-bold">{apiKeys.length}</div>
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardBody>
              {renderContent()}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
